import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Users, Film, TrendingUp, CheckCircle2, XCircle, Loader2, Trash2, Crown, Wand2, ImagePlay, Repeat } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-5">
    <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 bg-secondary/60 ${color}`}><Icon className="w-5 h-5" /></div>
    <p className="text-3xl font-display font-extrabold">{value}</p>
    <p className="text-muted-foreground text-sm mt-1">{label}</p>
  </motion.div>
);

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [videos, setVideos] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadVideos = () => api.get("/admin/videos").then((r) => setVideos(r.data)).catch(() => {});

  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    if (tab === "users" && users === null) loadUsers();
    if (tab === "videos" && videos === null) loadVideos();
  }, [tab]); // eslint-disable-line

  const toggleSub = async (u) => {
    const next = u.subscription_status === "active" ? "free" : "active";
    try {
      await api.patch(`/admin/users/${u.id}/subscription`, { subscription_status: next });
      toast.success(`${u.email} set to ${next}`);
      loadUsers(); loadStats();
    } catch { toast.error("Update failed"); }
  };

  const delVideo = async (id) => {
    try { await api.delete(`/admin/videos/${id}`); toast.success("Video removed"); loadVideos(); loadStats(); }
    catch { toast.error("Delete failed"); }
  };

  const TABS = [["overview", "Overview"], ["users", "Users"], ["videos", "Videos"]];

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-accent/15 grid place-items-center glow-pink"><Crown className="w-5 h-5 text-accent" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Admin Console</h1>
          <p className="text-muted-foreground text-sm">Manage users, content and platform activity.</p>
        </div>
      </div>

      <div className="glass rounded-full p-1 inline-flex mb-8">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        !stats ? <p className="text-muted-foreground">Loading…</p> : (
          <div className="space-y-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total users" value={stats.total_users} color="text-primary" />
              <StatCard icon={Crown} label="Creator subscribers" value={stats.subscribers} color="text-accent" />
              <StatCard icon={CheckCircle2} label="Videos generated" value={stats.completed} color="text-primary" />
              <StatCard icon={TrendingUp} label="Total jobs" value={stats.total_jobs} color="text-accent" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="glass rounded-2xl p-5 flex items-center gap-3"><Loader2 className="w-5 h-5 text-primary" /><div><p className="text-xl font-bold">{stats.processing}</p><p className="text-xs text-muted-foreground">In progress</p></div></div>
              <div className="glass rounded-2xl p-5 flex items-center gap-3"><XCircle className="w-5 h-5 text-destructive" /><div><p className="text-xl font-bold">{stats.failed}</p><p className="text-xs text-muted-foreground">Failed</p></div></div>
              <div className="glass rounded-2xl p-5 flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-primary" /><div><p className="text-xl font-bold">{stats.completed}</p><p className="text-xs text-muted-foreground">Completed</p></div></div>
            </div>
            <div className="glass rounded-2xl p-6">
              <h3 className="font-display font-bold mb-4">Generations by mode</h3>
              <div className="grid grid-cols-3 gap-4">
                {[["text", Wand2, "Text"], ["image", ImagePlay, "Image"], ["remix", Repeat, "Remix"]].map(([k, Icon, l]) => (
                  <div key={k} className="text-center">
                    <Icon className="w-6 h-6 mx-auto mb-2 text-primary" />
                    <p className="text-2xl font-display font-extrabold">{stats.by_mode[k] ?? 0}</p>
                    <p className="text-xs text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {tab === "users" && (
        !users ? <p className="text-muted-foreground">Loading…</p> : (
          <div className="glass rounded-2xl overflow-hidden" data-testid="admin-users">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">User</th>
                    <th className="text-left px-5 py-3 font-semibold">Role</th>
                    <th className="text-left px-5 py-3 font-semibold">Plan</th>
                    <th className="text-left px-5 py-3 font-semibold">Videos</th>
                    <th className="text-right px-5 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="px-5 py-3">
                        <p className="font-medium">{u.name || u.email.split("@")[0]}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </td>
                      <td className="px-5 py-3 capitalize">{u.role}</td>
                      <td className="px-5 py-3">
                        {u.subscription_status === "active"
                          ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent/15 text-accent">Creator</span>
                          : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">Free</span>}
                      </td>
                      <td className="px-5 py-3">{u.video_count}</td>
                      <td className="px-5 py-3 text-right">
                        <Button size="sm" variant="outline" className="rounded-full border-border text-xs h-8"
                          data-testid={`toggle-sub-${u.id}`} onClick={() => toggleSub(u)} disabled={u.role === "admin"}>
                          {u.subscription_status === "active" ? "Revoke" : "Grant Creator"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {tab === "videos" && (
        !videos ? <p className="text-muted-foreground">Loading…</p> : videos.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-muted-foreground"><Film className="w-8 h-8 mx-auto mb-3 opacity-60" />No videos generated yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-videos">
            {videos.map((v) => (
              <div key={v.id} className="glass rounded-2xl overflow-hidden">
                <video src={mediaUrl(v.video_url)} className="w-full h-44 object-cover bg-black/40" muted loop
                  onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} />
                <div className="p-4">
                  <p className="text-sm font-medium line-clamp-2 mb-1">{v.prompt}</p>
                  <p className="text-xs text-muted-foreground mb-3">{v.user_email} · {v.mode} · {v.duration}s</p>
                  <Button size="sm" variant="outline" className="w-full rounded-full border-border text-xs h-8 text-destructive hover:text-destructive"
                    data-testid={`del-video-${v.id}`} onClick={() => delVideo(v.id)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
